/**
 * Krok SEZ1 bod 2 — JEDNO TLACITKO „ZAPSAT" (R.B. 29. 10. 2026, Realman
 * prohlidka, bod 9).
 *
 * Zadani: „u klienta a u zakazky jedno tlacitko Zapsat s vyberem typu
 * (Jednani, Telefonat, Nekomunikuje/nezveda, Poznamka, Prohlidka, Podpis
 * dokumentu, Odlozit, Ukol, Odhad ceny); kazdy typ otevre kratky formular
 * a zapise aktivitu do historie — EXISTUJICI aktivity/ukoly, zadny novy
 * paralelni model".
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTI SEZ1-ZAPIS — do CEHO se zapisuje
 *
 * REOS uz dva zapisy „co se stalo" ma a oba se ctou na obrazovkach:
 *  1. `crm_tasks` (`POST /v1/crm/tasks`) — ma `party_id` i `case_id`.
 *     DOKONCENY ukol je to, z ceho seznam klientu pocita POSLEDNI KONTAKT
 *     a OTEVRENY ukol s terminem DALSI AKCI (`lib/client-activity.ts`).
 *     Ulozeni se statusem `done` nastavuje `completed_at` uz na serveru
 *     (`insertTaskRow` v @reos/crm), takze „posledni kontakt" sedi hned.
 *  2. `case_events` kind `note` (`POST /v1/cases/:id/notes`) — historie
 *     ZAKAZKY; `caseEventSentence` z nej uz umi lidskou vetu, takze zapis
 *     je videt v „Poslední dění" i na zalozce Aktivity.
 *
 * Proto: co se STALO, jde u zakazky do historie zakazky (1 zapis), u klienta
 * jako dokonceny ukol (1 zapis) — strana zadnou vlastni historii nema a
 * vymyslet pro ni treti model zadani vyslovne zakazuje. Co se ma STAT
 * (Ukol, Odlozit), jde v obou pripadech do ukolu s terminem, aby to vyskocilo
 * na Dnesku a v tabuli. Jeden zapis na jedno kliknuti — zadny soubeh dvou
 * volani, ktery by mohl skoncit v pulce.
 *
 * Tenhle modul je ciste vypocetni (bez `fetch`, bez `Date.now()`): rozhoduje
 * o TVARU zapisu, samo volani API je v `record-activity-api.ts`.
 * ---------------------------------------------------------------------------
 */

import { t } from '@reos/i18n';

const cs = { locale: 'cs' } as const;

export type ActivityKind =
  | 'meeting'
  | 'call'
  | 'no_answer'
  | 'note'
  | 'viewing'
  | 'document_signed'
  | 'postpone'
  | 'task'
  | 'price_estimate';

export interface ActivityKindSpec {
  readonly kind: ActivityKind;
  /** Popisek v nabidce i v zapsane vete. */
  readonly label: string;
  /**
   * `true` = zapis o tom, co se MA STAT (otevreny ukol s terminem).
   * `false` = zapis o tom, co se STALO (dokonceny ukol / poznamka v historii).
   */
  readonly plans: boolean;
  /** Formular zada cenu misto textu (Odhad ceny). */
  readonly asksAmount: boolean;
  /** Bez textu by zapis nemel nazev (Ukol). */
  readonly requiresText: boolean;
  /** Napoveda nad textovym polem — kratky formular ma rict, co do nej patri. */
  readonly placeholder: string;
}

/**
 * Poradi = poradi v nabidce „Co chcete zapsat". Je to poradi ze zadani
 * (nejcastejsi zapisy nahore), ne abeceda.
 */
export const ACTIVITY_KINDS: readonly ActivityKindSpec[] = [
  { kind: 'meeting', plans: false, asksAmount: false, requiresText: false, label: t('webAdmin.recordActivity.kind.meeting', cs), placeholder: t('webAdmin.recordActivity.placeholder.meeting', cs) },
  { kind: 'call', plans: false, asksAmount: false, requiresText: false, label: t('webAdmin.recordActivity.kind.call', cs), placeholder: t('webAdmin.recordActivity.placeholder.call', cs) },
  { kind: 'no_answer', plans: false, asksAmount: false, requiresText: false, label: t('webAdmin.recordActivity.kind.noAnswer', cs), placeholder: t('webAdmin.recordActivity.placeholder.noAnswer', cs) },
  { kind: 'note', plans: false, asksAmount: false, requiresText: true, label: t('webAdmin.recordActivity.kind.note', cs), placeholder: t('webAdmin.recordActivity.placeholder.note', cs) },
  { kind: 'viewing', plans: false, asksAmount: false, requiresText: false, label: t('webAdmin.recordActivity.kind.viewing', cs), placeholder: t('webAdmin.recordActivity.placeholder.viewing', cs) },
  { kind: 'document_signed', plans: false, asksAmount: false, requiresText: false, label: t('webAdmin.recordActivity.kind.documentSigned', cs), placeholder: t('webAdmin.recordActivity.placeholder.documentSigned', cs) },
  { kind: 'postpone', plans: true, asksAmount: false, requiresText: false, label: t('webAdmin.recordActivity.kind.postpone', cs), placeholder: t('webAdmin.recordActivity.placeholder.postpone', cs) },
  { kind: 'task', plans: true, asksAmount: false, requiresText: true, label: t('webAdmin.recordActivity.kind.task', cs), placeholder: t('webAdmin.recordActivity.placeholder.task', cs) },
  { kind: 'price_estimate', plans: false, asksAmount: true, requiresText: false, label: t('webAdmin.recordActivity.kind.priceEstimate', cs), placeholder: t('webAdmin.recordActivity.placeholder.priceEstimate', cs) },
];

export function activityKindSpec(kind: ActivityKind): ActivityKindSpec {
  const spec = ACTIVITY_KINDS.find((item) => item.kind === kind);
  // Vycet je uzavreny (`ActivityKind`), takze tohle je jen pojistka typu.
  if (spec === undefined) throw new Error(`neznamy typ zapisu: ${kind}`);
  return spec;
}

export interface ActivityDraft {
  readonly kind: ActivityKind;
  /** Co makler napsal; u Odhadu ceny zustava prazdny. */
  readonly text: string;
  /** Termin z `<input type="date">` jako ISO, nebo `null` (bez terminu). */
  readonly dueAt: string | null;
  /** Cena jak ji makler napsal — do zapisu jde jako TEXT, necita se s ni. */
  readonly amount: string;
}

export interface ActivityRecord {
  readonly kind: ActivityKind;
  /** Nazev ukolu (limit API je 300 znaku). */
  readonly title: string;
  readonly description: string | null;
  readonly status: 'todo' | 'done';
  readonly dueAt: string | null;
  /** Jedna veta do historie zakazky (limit API je 5000 znaku). */
  readonly historyText: string;
}

/** Limity jsou SERVEROVE (`createTaskBodySchema`, `noteBodySchema`). */
const TITLE_MAX = 300;
const HISTORY_MAX = 5000;

function clamp(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/** Prvni radek textu — nazev ukolu je jednoradkovy, popis nese cely text. */
function firstLine(text: string): string {
  return (text.split('\n')[0] ?? '').trim();
}

export function activityDetail(draft: ActivityDraft): string {
  const spec = activityKindSpec(draft.kind);
  if (spec.asksAmount) {
    const amount = draft.amount.trim();
    return amount === '' ? '' : t('webAdmin.recordActivity.amountDetail', { ...cs, values: { amount } });
  }
  return draft.text.trim();
}

/**
 * Tvar zapisu pro jeden typ. Nazev i veta do historie vzdy ZACINAJI typem
 * („Telefonát", „Nekomunikuje / nezvedá") — v tabuli ukolu i v historii pak
 * makler pozna, co se stalo, i bez rozkliknuti. Oddelovac je DVOJTECKA, ne
 * dlouha pomlcka (pravidlo POMLC1, hlida `packages/platform/i18n/test`).
 */
export function buildActivityRecord(draft: ActivityDraft): ActivityRecord {
  const spec = activityKindSpec(draft.kind);
  const detail = activityDetail(draft);
  const headline = firstLine(detail);
  const title = clamp(headline === '' ? spec.label : `${spec.label}: ${headline}`, TITLE_MAX);
  const historyText = clamp(detail === '' ? spec.label : `${spec.label}: ${detail}`, HISTORY_MAX);
  return {
    kind: draft.kind,
    title,
    /* Popis nese CELY text (nazev je jen prvni radek) — jinak by se delsi
       zapis tise ztratil. Prazdny text = `null`, ne prazdny retezec: API
       `description` validuje jako neprazdny, kdyz prijde. */
    description: detail === '' ? null : detail,
    status: spec.plans ? 'todo' : 'done',
    dueAt: spec.plans ? draft.dueAt : null,
    historyText,
  };
}

/** Co formular jeste nema, aby sel odeslat; `null` = jde zapsat. */
export function activityDraftProblem(draft: ActivityDraft): 'text' | 'amount' | null {
  const spec = activityKindSpec(draft.kind);
  if (spec.asksAmount) return draft.amount.trim() === '' ? 'amount' : null;
  if (spec.requiresText && draft.text.trim() === '') return 'text';
  return null;
}
